import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { X, ShieldCheck, Zap, Save, Info } from "lucide-react";
import { useSelector, useDispatch } from "react-redux"; // <-- Added useDispatch
import {
  selectUserID,
  SET_SalesOrderConfig,
} from "../../../redux/IchthusSlice"; // <-- Added SET_SalesOrderConfig
import { domain } from "../../../security";

const QUOTATION_OPTIONS = [
  {
    value: true,
    label: "With Sales Quotation",
    description: "Require a Sales Quotation before proceeding to Sales Order.",
    color: "emerald",
    icon: ShieldCheck,
  },
  {
    value: false,
    label: "Without Sales Quotation",
    description: "Directly create a Sales Order without a prior quotation.",
    color: "blue",
    icon: Zap,
  },
];

const COLOR = {
  emerald: {
    checked: "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200/60",
    unchecked:
      "border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/30",
    icon: "text-emerald-600",
    checkBox: "bg-emerald-500 border-emerald-500",
    label: "text-emerald-800",
  },
  blue: {
    checked: "border-blue-400 bg-blue-50 ring-2 ring-blue-200/60",
    unchecked:
      "border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/30",
    icon: "text-blue-600",
    checkBox: "bg-blue-500 border-blue-500",
    label: "text-blue-800",
  },
};

const RadioCard = ({ option, checked, onChange }) => {
  const c = COLOR[option.color];
  const Icon = option.icon;

  return (
    <button
      type="button"
      onClick={() => onChange(option.value)}
      className={`w-full text-left rounded-xl border-2 p-4 transition-all duration-200 cursor-pointer select-none ${
        checked ? c.checked : c.unchecked
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
            checked ? c.checkBox : "border-slate-300 bg-white"
          }`}
        >
          {checked && <div className="w-2 h-2 rounded-full bg-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Icon size={15} className={c.icon} />
            <span
              className={`text-sm font-semibold ${checked ? c.label : "text-slate-700"}`}
            >
              {option.label}
            </span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            {option.description}
          </p>
        </div>
      </div>
    </button>
  );
};

const AddSalesOrderSettings = ({ currentSetting, onClose, refreshData }) => {
  const currentUserID = useSelector(selectUserID);
  const dispatch = useDispatch(); // <-- Added dispatch
  const [isSaving, setIsSaving] = useState(false);
  const [withSalesQuotation, setWithSalesQuotation] = useState(currentSetting);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await axios.put(`${domain}/api/SalesOrderSettings`, {
        withSalesQuotation: withSalesQuotation,
        userID: currentUserID,
      });

      // --> ADD THIS: Sync the new value instantly to the Redux global state
      dispatch(SET_SalesOrderConfig(withSalesQuotation));

      toast.success("Configuration updated successfully!");
      refreshData();
      onClose();
    } catch {
      toast.error("Failed to update configuration.");
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col w-full relative">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white">
        <div>
          <h2 className="text-base font-semibold text-slate-800 tracking-tight">
            Edit Global Quotation Rule
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Choose the quotation behavior for the entire system
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* ── Body ── */}
      <div className="p-6">
        <form id="editConfigForm" onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {QUOTATION_OPTIONS.map((opt) => (
              <RadioCard
                key={opt.label}
                option={opt}
                checked={withSalesQuotation === opt.value}
                onChange={(val) => setWithSalesQuotation(val)}
              />
            ))}
          </div>

          <div className="mt-4 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-3">
            <Info size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
            <span>
              Warning: Changing this setting applies immediately to all users.
              Make sure your team is informed of this workflow change.
            </span>
          </div>
        </form>
      </div>

      {/* ── Footer ── */}
      <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 transition-all duration-200"
        >
          Cancel
        </button>
        <button
          form="editConfigForm"
          type="submit"
          disabled={isSaving}
          className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
        >
          <Save size={15} />
          {isSaving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default AddSalesOrderSettings;
